export const CHART_GRID_COLOR_DARK = "#4B5462";
export const CHART_GRID_COLOR_LIGHT = "#EFEFEF";
export const CHART_GRID_COLOR_LIGHT_RGBA = "rgba(156, 163, 175, 0.45)";
export const CHART_PLOT_BG_DARK = "#374151";

export function chartGridColor(isDarkMode) {
  return isDarkMode ? CHART_GRID_COLOR_DARK : CHART_GRID_COLOR_LIGHT;
}

export function plotlyThemeColors(isDarkMode) {
  return {
    tick: isDarkMode ? "#E5E7EB" : "#374151",
    title: isDarkMode ? "#F9FAFB" : "#111827",
    paperBg: isDarkMode ? "transparent" : "#ffffff",
    plotBg: isDarkMode ? CHART_PLOT_BG_DARK : "#ffffff",
    sceneBg: isDarkMode ? "#1F2937" : "#ffffff",
    grid: chartGridColor(isDarkMode),
  };
}

function themeAxis(axis, colors) {
  if (!axis) return axis;
  const next = { ...axis };
  if (next.showgrid !== false) {
    next.showgrid = true;
    next.gridcolor = colors.grid;
    next.gridwidth = next.gridwidth ?? 0.5;
  }
  if (next.minor && next.minor.showgrid) {
    next.minor = {
      ...next.minor,
      gridcolor: colors.grid,
      gridwidth: next.minor.gridwidth ?? 0.5,
    };
  }
  next.zerolinecolor = colors.grid;
  next.tickfont = { ...(next.tickfont || {}), color: colors.tick };

  if (next.title !== undefined && next.title !== null) {
    if (typeof next.title === "string") {
      next.title = { text: next.title, font: { color: colors.title } };
    } else {
      next.title = {
        ...next.title,
        font: { ...(next.title.font || {}), color: colors.title },
      };
    }
  }
  return next;
}

/** Apply shared dark/light colors to a Plotly layout object (in-place copy). */
export function mergePlotlyTheme(layout, isDarkMode) {
  if (!layout || typeof layout !== "object") return layout;

  const colors = plotlyThemeColors(isDarkMode);
  const out = { ...layout };

  out.paper_bgcolor = colors.paperBg;
  out.plot_bgcolor = colors.plotBg;
  out.font = { ...(out.font || {}), color: colors.tick };

  if (out.xaxis) out.xaxis = themeAxis(out.xaxis, colors);
  if (out.yaxis) out.yaxis = themeAxis(out.yaxis, colors);

  if (out.scene) {
    out.scene = { ...out.scene, bgcolor: colors.sceneBg };
    if (out.scene.xaxis) out.scene.xaxis = themeAxis(out.scene.xaxis, colors);
    if (out.scene.yaxis) out.scene.yaxis = themeAxis(out.scene.yaxis, colors);
    if (out.scene.zaxis) out.scene.zaxis = themeAxis(out.scene.zaxis, colors);
  }

  if (out.legend) {
    out.legend = {
      ...out.legend,
      font: { ...(out.legend.font || {}), color: colors.tick },
    };
    if (out.legend.title !== undefined && out.legend.title !== null) {
      if (typeof out.legend.title === "string") {
        out.legend.title = { text: out.legend.title, font: { color: colors.title } };
      } else {
        out.legend.title = {
          ...out.legend.title,
          font: { ...(out.legend.title.font || {}), color: colors.title },
        };
      }
    }
  }

  return out;
}

export function qaqcTableMainHeaderStyle(isDarkMode) {
  if (!isDarkMode) return undefined;
  return {
    backgroundColor: "var(--dark-mode-table-header)",
    color: "var(--dark-mode-text)",
    borderColor: "var(--dark-mode-table-border-header)",
  };
}

export function qaqcTableSubHeaderStyle(isDarkMode) {
  return qaqcTableMainHeaderStyle(isDarkMode);
}

export function applyPlotlyGridLineStroke(chartElement, isDarkMode, options = {}) {
  if (!chartElement?.querySelector) return;
  const color = chartGridColor(isDarkMode);
  const opacity = options.opacity ?? "0.6";
  const strokeWidth = options.strokeWidth ?? "0.5px";
  const selector =
    options.selector ?? ".xgrid line, .ygrid line, .zgrid line, .cartesianlayer .gridlayer line";

  chartElement.querySelectorAll(selector).forEach((line) => {
    line.style.stroke = color;
    line.setAttribute("stroke", color);
    if (opacity != null) {
      line.style.opacity = opacity;
      line.setAttribute("opacity", opacity);
    }
    if (strokeWidth != null) {
      line.style.strokeWidth = strokeWidth;
      line.setAttribute("stroke-width", strokeWidth);
    }
  });
}

/** Force Plotly plot-area rects to match dark/light theme (log axes may reset fill). */
export function applyPlotlyPlotBackground(chartElement, isDarkMode) {
  if (!chartElement?.querySelector) return;

  const colors = plotlyThemeColors(isDarkMode);
  const fill = colors.plotBg === "transparent" ? "transparent" : colors.plotBg;

  chartElement.querySelectorAll(".plotly rect.bg, .plotly g.plot > rect").forEach((rect) => {
    rect.setAttribute("fill", fill);
    rect.style.fill = fill;
  });

  const svgContainer = chartElement.querySelector(".svg-container");
  if (svgContainer) {
    svgContainer.style.background = isDarkMode ? "transparent" : "";
  }
}

export function getPlotlyBackgroundRelayout(isDarkMode) {
  const colors = plotlyThemeColors(isDarkMode);
  return {
    plot_bgcolor: colors.plotBg,
    paper_bgcolor: colors.paperBg,
  };
}
